import { z } from 'zod';
import { aiUsageSchema } from './ai.js';
import { foodSourceSchema, macroSetSchema } from './food.js';
/* ==========================================================================
 *  Extraction pipeline contract (ISSUE-021 / ISSUE-022 / ISSUE-023)
 *
 *  The product's defining feature: free text → structured ledger entries via a
 *  fixed 7-stage pipeline (03-architecture.md §AI pipeline):
 *
 *    1. intent      → what kind of log is this?            (AI, small model)
 *    2. entities    → foods / metrics / goals / notes      (AI, small model, tool-call)
 *    3. resolution  → each food → canonical food_id        (FoodResolver, ISSUE-014)
 *    4. macros      → absolute macros per portion          (PURE CODE, FR-5.4)
 *    5. validation  → sanity rules flag/block              (PURE CODE, ISSUE-022)
 *    6. persistence → raw_log + entries with provenance    (ISSUE-023)
 *    7. summary     → enqueue day/week delta               (ISSUE-030 / pg-boss)
 *
 *  CONTRACT TYPES only — zod is the single source of truth. The web app renders
 *  these (chips, clarifications, validation notes), so nothing here may import a
 *  provider SDK or anything server-only. The orchestrator that *runs* the stages
 *  lives in the server-only `@nutians/pipeline` package (see ADR-0002).
 *
 *  Invariant (FR-5.4): the AI layer extracts INTENT and ENTITIES only. Every
 *  number downstream — gram normalization, macro totals, validation — is pure,
 *  deterministic code so the ledger never depends on a model getting arithmetic
 *  right.
 * ========================================================================== */
/* ----- Stage 1: intent --------------------------------------------------- */
/** What the user's message is doing. `mixed` covers "ate eggs, weight 80kg".
 *  `question` routes to chat (ISSUE-040) instead of the write path.
 *  ADR-0013 Phase A adds four new intents handled by dedicated handlers (no AI). */
export const logIntentSchema = z.enum([
    // Existing — unchanged meaning
    'meal', // "I ate X" — food log, goes through full pipeline
    'weight', // "I weigh 78 kg" — body metric
    'goal', // "my goal is to eat 150 g protein"
    'note', // unstructured memo
    'question', // question to coach — routed to chat (/me/chat redirect)
    'mixed', // multiple intents in one message
    // New (ADR-0013 Phase A) — bypass AI extraction, handled by dedicated seams
    'set_metric', // "I'm at 80g protein today" — override metric total
    'adjust_metric', // "add 200 kcal" — delta on top of current
    'correct_entry', // "that salmon was 200g not 160" — edit last/specific entry
    'remove_entry', // "delete my last meal"
]);
/* ----- ADR-0013 §4.1: canonical metric keys ------------------------------ */
/** Canonical keys that match DaySummary.consumed (day-summary.ts). */
export const metricKeySchema = z.enum(['kcal', 'proteinG', 'carbsG', 'fatG', 'fiberG']);
/** Map user-spoken metric words → canonical DaySummary.consumed keys. */
export function normalizeMetric(word) {
    switch (word.toLowerCase()) {
        case 'protein':
            return 'proteinG';
        case 'carb':
        case 'carbs':
            return 'carbsG';
        case 'fat':
            return 'fatG';
        case 'fiber':
        case 'fibre':
            return 'fiberG';
        case 'cal':
        case 'cals':
        case 'calorie':
        case 'calories':
        case 'kcal':
            return 'kcal';
        default:
            return null;
    }
}
/* ----- ADR-0013 §5.3: per-entry resolution provenance -------------------- */
/** How a meal entry's macros were resolved. Drives the mobile badge and admin
 *  hit-rate dashboard. Separate from Stage-0 telemetry's coarse resolvedBy field. */
export const resolvedBySchema = z.enum([
    'deterministic', // M-B1 deterministic match
    'user_override', // user_food_override / user_mention_override
    'open_food_facts', // OFF adapter
    'usda', // USDA adapter
    'ai_haiku', // AI estimate, free tier
    'ai_sonnet', // AI estimate, premium tier
    'manual_adjustment', // synthetic entry from adjust_metric (§7)
    'user_direct', // user-supplied directMacros (§8.3)
    'unresolved', // resolver could not match — persisted as a visible, editable 0-macro entry (L1.1 / §14)
    'user_stated', // macros parsed by AI from log prose (L1.2 / §15); band=review, confidence=extraction.confidence
    'vision', // ADR-0014 V-1: macros resolved from a food photo via Pixtral
]);
/** Coerce a raw DB `meal_entry.resolved_by` value to a valid {@link ResolvedBy}
 *  or null. The column is unconstrained `text`, so legacy/buggy rows can hold
 *  values the view schema rejects. Coercing in the serializer keeps
 *  `mealEntryViewSchema` strict (and thus zod-to-openapi-convertible) while
 *  ensuring one bad row never 500s a serializer (NUTIANS-API-C / -B). */
export function coerceResolvedBy(value) {
    return resolvedBySchema.options.includes(value) ? value : null;
}
/** Provenance values whose macros are authored by the USER, not derived from a
 *  food-DB record + scaling. Used to exempt these entries from resolution-data-quality
 *  validation rules (those rules exist to catch bad DB/resolver data, not to
 *  second-guess numbers the user explicitly supplied — L1.2 / ADR-0013 §15.3).
 *  - `user_stated`      — macros parsed from the log message (L1.2 / §15)
 *  - `user_direct`      — macros typed into the edit sheet / directMacros API (§8.3)
 *  - `manual_adjustment`— synthetic entry from adjust_metric (§7) */
const USER_AUTHORED_RESOLVED_BY = new Set([
    'user_stated',
    'user_direct',
    'manual_adjustment',
]);
/** True when an entry's macros were authored by the user (see {@link USER_AUTHORED_RESOLVED_BY}).
 *  Null/undefined (legacy/backfill rows) are treated as NOT user-authored. */
export function isUserAuthored(resolvedBy) {
    return resolvedBy != null && USER_AUTHORED_RESOLVED_BY.has(resolvedBy);
}
/* ----- ADR-0013 §4.2: intent classifier types ---------------------------- */
/** Schema used for the Haiku fallback generateObject call (64 tokens). */
export const intentClassificationSchema = z.object({
    intent: logIntentSchema,
});
/* ----- Stage 2: entity extraction (tool-call output, FR-3.6) ------------- */
export const entityTypeSchema = z.enum(['food', 'body_metric', 'goal', 'note']);
/** Stated macros on a food entity — absolute totals for the logged line, never per-unit.
 *  Any subset may be present (partial is fine); unstated fields default to 0 in the entry.
 *  L1.2 / ADR-0013 §15. */
export const statedMacrosSchema = z.object({
    kcal: z.number().nonnegative().optional(),
    proteinG: z.number().nonnegative().optional(),
    carbsG: z.number().nonnegative().optional(),
    fatG: z.number().nonnegative().optional(),
});
/** A food mention as the model parsed it — BEFORE resolution/macros. `quantity`
 *  and `unit` are nullable: a missing quantity triggers a default-portion
 *  estimate (FR-3.4) or a clarification, never a guessed macro total. */
export const extractedFoodSchema = z.object({
    type: z.literal('food'),
    name: z.string().min(1), // "grilled chicken breast"
    quantity: z.number().positive().nullable().default(null),
    unit: z.string().nullable().default(null), // "g" | "ml" | "cup" | "slice" | null
    preparation: z.string().nullable().default(null), // "cooked" | "raw" | "fried"
    timestampHint: z.string().nullable().default(null), // "breakfast" | "2pm" | "yesterday"
    /** Absolute macro totals stated in the log message. When present, the resolver is
     *  bypassed entirely and the entry is created as `user_stated` (L1.2 / ADR-0013 §15). */
    statedMacros: statedMacrosSchema.nullable().optional(),
});
export const bodyMetricKindSchema = z.enum([
    'weight',
    'body_fat',
    'waist',
    'hip',
    'neck',
    'chest',
    'arm',
    'thigh',
]);
export const extractedBodyMetricSchema = z.object({
    type: z.literal('body_metric'),
    metric: bodyMetricKindSchema,
    value: z.number(),
    unit: z.string(), // "kg" | "lb" | "%" | "cm" | "in"
    timestampHint: z.string().nullable().default(null),
});
/** Freeform at MVP — structured goal parsing is a later issue. */
export const extractedGoalSchema = z.object({
    type: z.literal('goal'),
    text: z.string().min(1),
});
export const extractedNoteSchema = z.object({
    type: z.literal('note'),
    text: z.string().min(1),
});
export const extractedEntitySchema = z.discriminatedUnion('type', [
    extractedFoodSchema,
    extractedBodyMetricSchema,
    extractedGoalSchema,
    extractedNoteSchema,
]);
/** The structured output of stages 1+2 in a single tool call (FR-3.6). This is
 *  the Zod schema handed to `AiClient.generateObject` for extraction. */
export const extractionResultSchema = z.object({
    intent: logIntentSchema,
    entities: z.array(extractedEntitySchema).default([]),
    /** Model's self-reported confidence in the overall parse (0–1). Combined with
     *  resolution confidence to band each entry (see `classifyConfidence`). */
    confidence: z.number().min(0).max(1).default(0.5),
});
/** AI macro-estimate for a single DB-unresolved food. Macros are ABSOLUTE TOTALS
 *  for the logged portion (not per-100g) — mirrors the `user_stated` convention,
 *  so no scaling math touches the model (FR-5.4: AI sources values, code never
 *  does arithmetic). `index` maps the estimate back to its input entry by position. */
export const estimatedMacroSchema = z.object({
    index: z.number().int().nonnegative(),
    kcal: z.number().nonnegative(),
    proteinG: z.number().nonnegative(),
    carbsG: z.number().nonnegative(),
    fatG: z.number().nonnegative(),
    fiberG: z.number().nonnegative(),
});
/** Structured output of the `estimation` step (batched — one call per log).
 *  Schema handed to `AiClient.generateObject`. Items the model can't reasonably
 *  estimate are simply omitted, leaving those entries unresolved. */
export const estimationResultSchema = z.object({
    estimates: z.array(estimatedMacroSchema).default([]),
});
/* ----- Confidence banding (FR-3.3 / FR-3.5) ------------------------------ */
/** What the UI does with an entry, derived from its blended confidence:
 *  - `auto_accept` → persisted silently.
 *  - `review`      → persisted, shown with a review chip the user can confirm/edit.
 *  - `clarify`     → NOT auto-persisted; surfaces an inline clarification. */
export const confidenceBandSchema = z.enum(['auto_accept', 'review', 'clarify']);
/** The single tuning knob for FR-3.3 / FR-3.5. Kept here (not buried in the
 *  orchestrator) so thresholds are visible, testable, and swappable per eval
 *  (ISSUE-103). */
export const CONFIDENCE_THRESHOLDS = {
    /** >= this → auto-accept, no chip. */
    autoAccept: 0.85,
    /** >= this (and < autoAccept) → persist with a review chip (FR-3.5). */
    review: 0.5,
    // < review → clarify inline (FR-3.3); do not auto-persist.
};
/** Pure band classifier. Single source of truth for the auto-persist decision. */
export function classifyConfidence(score) {
    if (score >= CONFIDENCE_THRESHOLDS.autoAccept)
        return 'auto_accept';
    if (score >= CONFIDENCE_THRESHOLDS.review)
        return 'review';
    return 'clarify';
}
/* ----- Stage 5: validation (ISSUE-022) ----------------------------------- */
/** `block` gates persistence (the entry becomes a clarification until the user
 *  confirms); `flag` attaches a chip but lets the entry persist. */
export const validationSeveritySchema = z.enum(['block', 'flag']);
/** A validator's verdict on one draft. Persisted into `extraction_metadata`
 *  and rendered as chips by the web app (which is why it lives in shared). */
export const validationNoteSchema = z.object({
    rule: z.string(), // "OutlierCaloriesRule"
    code: z.string(), // "outlier_calories" (stable, for UI/telemetry)
    severity: validationSeveritySchema,
    message: z.string(), // user-facing, e.g. "That's over 3000 kcal — confirm?"
    suggestion: z.string().nullable().default(null),
});
/* ----- Stage 4+5 output: drafts (pre-persistence) ------------------------ */
/** Lifecycle of a draft entry through stages 5–6:
 *  - `ready`               → will persist (auto_accept or review band).
 *  - `needs_clarification` → blocked on a user answer (clarify band / block rule).
 *  - `blocked`             → a `block` validation rule fired; awaits confirmation. */
export const entryStatusSchema = z.enum(['ready', 'needs_clarification', 'blocked']);
/** Coerce a raw DB `meal_entry.status` value to a valid {@link EntryStatus},
 *  defaulting to `ready`. The column is unconstrained `text`; see
 *  {@link coerceResolvedBy} for the rationale (NUTIANS-API-C / -B). */
export function coerceEntryStatus(value) {
    return entryStatusSchema.options.includes(value)
        ? value
        : 'ready';
}
/**
 * A food entry after resolution (stage 3) + deterministic macro estimation
 * (stage 4) + validation (stage 5), ready for persistence (stage 6).
 * `macros` are ABSOLUTE totals for the portion (not per-100) computed by
 * `scaleMacros` — never by the model.
 */
export const mealEntryDraftSchema = z.object({
    // resolution (stage 3)
    foodId: z.string().nullable().default(null), // canonical `${source}:${sourceId}`, null if unresolved
    name: z.string(), // resolved name, or the raw mention if unresolved
    source: foodSourceSchema.nullable().default(null),
    // portion (normalized to the food's base unit; grams for solids, ml for liquids)
    quantityG: z.number().nonnegative(),
    displayQuantity: z.number().positive().nullable().default(null), // what to show ("2")
    displayUnit: z.string().nullable().default(null), // ("slices")
    quantityIsDefault: z.boolean().default(false), // FR-3.4: a default portion was applied
    // macros (stage 4 — absolute totals, deterministic)
    macros: macroSetSchema,
    // scoring + validation
    confidence: z.number().min(0).max(1),
    band: confidenceBandSchema,
    status: entryStatusSchema,
    validationNotes: z.array(validationNoteSchema).default([]),
    /**
     * Propagated from the extracted entity's `timestampHint` (ISSUE-027).
     * Passed to `resolveTimestamp` in stage 6 so meal entries land on the
     * correct past date when the user logs "yesterday lunch" etc.
     * Stored in `extraction_metadata` for audit purposes.
     */
    timestampHint: z.string().nullable().default(null),
    /** Per-entry resolution provenance (ADR-0013 §5.3). Null for legacy/backfill rows. */
    resolvedBy: resolvedBySchema.nullable().optional(),
});
export const bodyMetricDraftSchema = z.object({
    metric: bodyMetricKindSchema,
    value: z.number(),
    unit: z.string(),
    recordedAtHint: z.string().nullable().default(null),
    confidence: z.number().min(0).max(1),
    band: confidenceBandSchema,
});
/* ----- Clarifications (FR-3.3, in-stream) -------------------------------- */
/** One option offered to disambiguate ("cooked" vs "dry", or a candidate food). */
export const clarificationOptionSchema = z.object({
    label: z.string(), // "cooked"
    foodId: z.string().nullable().default(null), // pick a specific candidate if known
});
export const clarificationKindSchema = z.enum(['select', 'confirm']);
/** A question surfaced inline; the user answers in-stream and the pipeline is
 *  re-invoked with the answer (see ADR-0002, decision 7). */
export const clarificationSchema = z.object({
    /** `select` is a multi-option disambiguation; `confirm` is a yes/no generic guess. */
    kind: clarificationKindSchema.optional(),
    entityName: z.string(), // "rice"
    question: z.string(), // "1 cup rice — cooked or dry?"
    options: z.array(clarificationOptionSchema).default([]),
});
/* ----- Pipeline result --------------------------------------------------- */
/** Per-stage timing + token telemetry (sub-task: per-step duration/tokens). */
export const pipelineStageTelemetrySchema = z.object({
    stage: z.string(), // "intent" | "entities" | "resolution" | ...
    durationMs: z.number().nonnegative(),
    inputTokens: z.number().int().nonnegative().default(0),
    outputTokens: z.number().int().nonnegative().default(0),
    /** ADR-0012 M-B1 / ADR-0014 V-1/V-2: which path served the extraction stage.
     *  Present only on 'extraction' / 'extraction-vision' telemetry entries.
     *  Used by the admin portal to compute the deterministic hit rate. */
    resolvedBy: z.enum(['deterministic', 'ai', 'vision', 'label']).optional(),
    /** Count of simple compound food phrases split into components. */
    decomposedCount: z.number().int().nonnegative().optional(),
    /** Count of candidate lists where generic-first reranking changed the winner. */
    genericBiasHits: z.number().int().nonnegative().optional(),
});
/** Everything one log produces. The route returns this to the web app: ready
 *  entries render as confirmed/chip rows, clarifications render as inline
 *  prompts. `usage` rolls up every AI call for cost attribution (D-16). */
export const pipelineResultSchema = z.object({
    intent: logIntentSchema,
    rawLogId: z.string().nullable().default(null), // set once stage 6 persists the raw_log
    mealEntries: z.array(mealEntryDraftSchema).default([]),
    bodyMetrics: z.array(bodyMetricDraftSchema).default([]),
    notes: z.array(z.string()).default([]),
    clarifications: z.array(clarificationSchema).default([]),
    usage: z.array(aiUsageSchema).default([]),
    telemetry: z.array(pipelineStageTelemetrySchema).default([]),
});
//# sourceMappingURL=pipeline.js.map